import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './ConduiteSupervisee.css';

const ConduiteSupervisee = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const avantages = [
    {
      icon: '✨',
      title: 'Plus souple que l\'AAC',
      description: 'Une formule plus flexible adaptée à vos besoins et à votre rythme'
    },
    {
      icon: '📈',
      title: 'Expérience renforcée',
      description: 'Acquérez davantage d\'expériences de conduite pour une meilleure préparation à l\'examen'
    },
    {
      icon: '🎯',
      title: 'Taux de réussite amélioré',
      description: 'Augmentez significativement vos chances de réussite lors de l\'épreuve pratique du permis'
    },
    {
      icon: '⏰',
      title: 'Flexibilité du démarrage',
      description: 'Dès la signature du contrat de formation ou après l\'obtention de l\'attestation de fin de formation initiale'
    },
    {
      icon: '👨‍🏫',
      title: 'Accompagnement personnalisé',
      description: 'Bénéficiez des conseils d\'un enseignant expert et d\'un accompagnateur de confiance'
    }
  ];

  const conditions = [
    {
      title: 'Conditions pour l\'élève',
      items: [
        'Avoir 18 ans ou plus',
        'Avoir réussi l\'épreuve théorique du code (ETG)',
        'Avoir suivi au moins 20 heures de conduite en école de conduite (13 heures si boîte automatique)',
        'Bénéficier d\'une autorisation de l\'enseignant en fonction de vos compétences et comportements',
        'Obtenir l\'accord préalable écrit de l\'assureur (extension de garantie)'
      ]
    },
    {
      title: 'Conditions pour l\'accompagnateur',
      items: [
        'Être titulaire du permis B depuis au moins 5 ans sans interruption',
        'Avoir l\'accord de l\'assureur',
        'Ne pas avoir de condamnation pour certains délits (alcool, drogues, délit de fuite...)',
        'Être mentionné dans le contrat signé avec l\'école de conduite',
        'Participer au rendez-vous préalable'
      ]
    }
  ];

  const etapes = [
    {
      numero: '1',
      titre: 'Inscription',
      description: 'Attestation d\'inscription au permis de conduire, livret d\'apprentissage, lettre d\'accord assurance',
      details: [
        'Attestation d\'inscription au permis de conduire',
        'Livret d\'apprentissage',
        'Lettre d\'accord assurance (extension de garantie)'
      ]
    },
    {
      numero: '2',
      titre: 'Fin de formation initiale',
      description: 'Formation théorique (ETG), formation pratique, bilan de compétences',
      details: [
        'Formation théorique',
        'Épreuve théorique générale (ETG - code)',
        'Formation pratique (20h minimum, 13h si boîte automatique)',
        'Bilan de compétences',
        'Attestation de fin de formation initiale'
      ]
    },
    {
      numero: '3',
      titre: 'Conduite supervisée',
      description: 'Rendez-vous préalable de 2 heures minimum, puis phase de conduite supervisée',
      details: [
        'Rendez-vous préalable de 2 heures minimum',
        'Expérience de conduite jugée suffisante par l\'enseignant',
        'Phase de conduite supervisée avec accompagnateur'
      ]
    },
    {
      numero: '4',
      titre: 'Épreuve pratique',
      description: 'Passage de l\'épreuve pratique du permis de conduire avec une préparation renforcée'
    }
  ];

  const reglesConduite = [
    {
      title: 'Réseau routier',
      content: 'Conduite sur le réseau routier et autoroutier du territoire national à différents moments du jour et de la nuit, par diverses conditions météorologiques'
    },
    {
      title: 'Équipement du véhicule',
      content: 'Véhicule équipé de 2 rétroviseurs latéraux (l\'accompagnateur peut ajouter un second rétroviseur droit et un second rétroviseur central)'
    },
    {
      title: 'Disque de conduite',
      content: 'Disque « conduite accompagnée » obligatoire à l\'arrière du véhicule'
    }
  ];

  const vitessesMax = [
    { route: '110 km/h', description: 'Sur les sections d\'autoroute limitées à 110 km/h' },
    { route: '100 km/h', description: 'Sur les routes à accès réglementé' },
    { route: '80 km/h', description: 'Sur les autres routes' }
  ];

  const documentsObligatoires = [
    'Attestation d\'inscription au permis de conduire',
    'Livret d\'apprentissage contenant l\'attestation de fin de formation initiale et la lettre-avenant signée par l\'assurance (peut être dématérialisé)',
    'Disque "Conduite Accompagnée" apposé à l\'arrière du véhicule',
    'Permis de conduire de l\'accompagnateur'
  ];

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <div className="conduite-supervisee-page">
      <section className="cs-hero">
        <div className="container">
          <div className="hero-content-cs">
            <h1 className="cs-title">Conduite Supervisée</h1>
            <p className="cs-subtitle">Plus souple que l'AAC • Dès 18 ans</p>
            <p className="hero-description">
              Inscrit dans une école de conduite, vous avez la possibilité de compléter votre formation initiale 
              par une phase de conduite supervisée vous permettant d'acquérir davantage d'expériences, 
              afin de passer ensuite l'épreuve pratique dans des conditions sereines.
            </p>
          </div>
        </div>
      </section>

      {/* Section Qu'est-ce que */}
      <section className="cs-intro-section">
        <div className="container">
          <div className="intro-content">
            <h2 className="section-title-main">Qu'est-ce que la conduite supervisée ?</h2>
            <div className="intro-cards">
              <div className="intro-card">
                <div className="intro-icon">📝</div>
                <h3>Formation initiale</h3>
                <p>
                  Comme pour la conduite accompagnée, vous devez avoir réussi l'épreuve du code et avoir suivi 
                  <strong> au moins 20 heures de conduite</strong> en école de conduite (13 heures si boîte automatique). 
                  À la fin de la formation initiale, c'est l'enseignant qui vous autorise à opter pour la conduite supervisée 
                  en fonction de vos compétences et comportements.
                </p>
              </div>
              <div className="intro-card">
                <div className="intro-icon">⏰</div>
                <h3>Quand commencer ?</h3>
                <p>
                  Vous pouvez choisir la conduite supervisée <strong>dès la signature du contrat de formation</strong> 
                  ou <strong>après l'obtention de l'attestation de fin de formation initiale</strong>. 
                  Une flexibilité qui s'adapte à votre situation.
                </p>
              </div>
              <div className="intro-card highlight">
                <div className="intro-icon">👥</div>
                <h3>Rendez-vous préalable</h3>
                <p>
                  D'une durée minimale de <strong>2 heures</strong>, le rendez-vous préalable réunit l'élève au poste de conduite, 
                  l'enseignant à ses côtés et l'accompagnateur à l'arrière pour réaliser le « passage de relais ». 
                  Un guide est remis à l'accompagnateur avec toutes les informations utiles.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Avantages */}
      <section className="cs-avantages-section">
        <div className="container">
          <h2 className="section-title-main">Pourquoi choisir la conduite supervisée ?</h2>
          <div className="avantages-grid-modern">
            {avantages.map((avantage, index) => (
              <div key={index} className="avantage-card-modern">
                <div className="avantage-icon-modern">{avantage.icon}</div>
                <h3 className="avantage-title-modern">{avantage.title}</h3>
                <p className="avantage-desc-modern">{avantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Conditions */}
      <section className="cs-conditions-section">
        <div className="container">
          <h2 className="section-title-main">Les conditions</h2>
          <div className="conditions-grid">
            {conditions.map((condition, index) => (
              <div key={index} className="condition-card">
                <h3 className="condition-card-title">{condition.title}</h3>
                <ul className="condition-list">
                  {condition.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="condition-item">
                      <span className="check-icon">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Quand choisir */}
      <section className="cs-quand-section">
        <div className="container">
          <h2 className="section-title-main">Quand choisir la conduite supervisée ?</h2>
            <div className="quand-cards">
            <div className="quand-card">
              <div className="quand-number">1</div>
              <h3>Dès la signature du contrat de formation</h3>
              <p>Vous pouvez opter pour la conduite supervisée dès votre inscription, en prévoyant cette phase dans votre parcours de formation.</p>
            </div>
            <div className="quand-card">
              <div className="quand-number">2</div>
              <h3>Après l'obtention de l'attestation de fin de formation initiale</h3>
              <p>Si vous avez terminé votre formation initiale, vous pouvez compléter votre apprentissage par la conduite supervisée pour renforcer votre expérience avant l'examen.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Étapes */}
      <section className="cs-etapes-section">
        <div className="container">
          <h2 className="section-title-main">Les étapes de la conduite supervisée</h2>
          <div className="etapes-timeline">
            {etapes.map((etape, index) => (
              <div key={index} className="etape-item">
                <div className="etape-numero">{etape.numero}</div>
                <div className="etape-content">
                  <h3 className="etape-titre">{etape.titre}</h3>
                  <p className="etape-description">{etape.description}</p>
                  {etape.details && (
                    <ul className="etape-details">
                      {etape.details.map((detail, detailIndex) => (
                        <li key={detailIndex}>{detail}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Règles de conduite */}
      <section className="cs-regles-section">
        <div className="container">
          <h2 className="section-title-main">Les règles de conduite</h2>
          <div className="regles-grid">
            {reglesConduite.map((regle, index) => (
              <div key={index} className="regle-card">
                <h3 className="regle-title">{regle.title}</h3>
                <p className="regle-content">{regle.content}</p>
              </div>
            ))}
          </div>
          
          <div className="vitesses-section">
            <h3 className="vitesses-title">Vitesses maximales autorisées</h3>
            <div className="vitesses-grid">
              {vitessesMax.map((vitesse, index) => (
                <div key={index} className="vitesse-card">
                  <div className="vitesse-value">{vitesse.route}</div>
                  <p className="vitesse-desc">{vitesse.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section Documents obligatoires */}
      <section className="cs-documents-section">
        <div className="container">
          <h2 className="section-title-main">Documents obligatoires</h2>
          <p className="documents-intro">
            Lors de la conduite supervisée, vous devez avoir avec vous :
          </p>
          <div className="documents-list">
            {documentsObligatoires.map((doc, index) => (
              <div key={index} className="document-item">
                <span className="document-icon">📋</span>
                <span className="document-text">{doc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Infos complémentaires */}
      <section className="cs-infos-section">
        <div className="container">
          <h2 className="section-title-main">Informations complémentaires</h2>
          <div className="infos-grid">
            <div className="info-card-modern">
              <h3 className="info-title-modern">Permis probatoire</h3>
              <p className="info-content-modern">
                6 points à l'obtention du permis (+2 points par an pendant 3 ans sans infraction). 
                Période probatoire de 3 ans. Conducteur novice : seuil d'alcoolémie à 0,2 g/l (0 verre d'alcool).
              </p>
            </div>
            <div className="info-card-modern">
              <h3 className="info-title-modern">Assurance</h3>
              <p className="info-content-modern">
                L'accord préalable écrit de l'assureur (extension de garantie) est indispensable. 
                Il permet de couvrir le ou les véhicules utilisés pour la conduite et ses occupants.
              </p>
            </div>
            <div className="info-card-modern">
              <h3 className="info-title-modern">Accompagnateurs multiples</h3>
              <p className="info-content-modern">
                Vous pouvez avoir plusieurs accompagnateurs, y compris hors du cadre familial. 
                Chaque accompagnateur doit respecter les conditions requises.
              </p>
            </div>
            <div className="info-card-modern">
              <h3 className="info-title-modern">Guide de l'accompagnateur</h3>
              <p className="info-content-modern">
                Un guide de 32 pages en couleurs est remis à l'accompagnateur lors du rendez-vous préalable. 
                Il contient toutes les informations utiles et des conseils à partager. Demandez-le à votre auto-école.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cs-cta-section">
        <div className="container">
          <h2 className="cta-title">Prêt à renforcer votre expérience ?</h2>
          <p className="cta-description">Contactez-nous pour en savoir plus sur la conduite supervisée et nos formules</p>
          <Link to="/contact" className="btn-cta-main">
            Nous contacter
          </Link>
        </div>
      </section>

      {/* Modal Tarifs */}
      {modalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleCloseModal}>×</button>
            <h3 className="modal-title">Tarif - Conduite Supervisée</h3>
            <div className="modal-price">
              <p className="price-label">À partir de</p>
              <p className="price-amount">Sur devis</p>
            </div>
            <p className="modal-description">
              Pour plus d'informations et connaître le tarif exact adapté à votre situation, 
              n'hésitez pas à nous contacter.
            </p>
            <Link to="/contact" className="btn btn-contact-modal">
              NOUS CONTACTER
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConduiteSupervisee;
